<template>
    <div class="calc-workflow-main">
        <!-- 조직 선택 (선택 전) -->
        <div v-show="!selectedOrg" class="org-selector">
            <div class="org-selector-card">
                <i class="fas fa-building org-selector-icon"></i>
                <h3 class="org-selector-title">조직을 선택해주세요</h3>
                <p class="org-selector-desc">조직을 선택하면 해당 조직의 워크플로우를 확인할 수 있습니다</p>
                <select v-model="selectedOrg" class="org-select" aria-label="조직 선택">
                    <option :value="null" disabled>조직 선택</option>
                    <option v-for="opt in filteredOrgOptions" :key="opt.value" :value="opt.value">
                        {{ opt.text }}
                    </option>
                </select>
            </div>
        </div>

        <!-- 조직 선택 후: 워크플로우 영역 (v-show로 항상 마운트 → 조직 미선택 시에도 신규 생성 모달 호출 가능) -->
        <div v-show="!!selectedOrg" class="calc-workflow-container">
            <div class="org-bar">
                <button
                    type="button"
                    class="org-bar-back"
                    :title="selectedStep ? '워크플로우·월별 산정으로' : '조직 선택으로'"
                    @click="handleOrgBarBack"
                >
                    <i class="fas fa-arrow-left"></i>
                </button>
                <i class="fas fa-building org-bar-icon"></i>
                <select v-model="selectedOrg" class="org-bar-select" aria-label="조직 선택">
                    <option v-for="opt in filteredOrgOptions" :key="opt.value" :value="opt.value">
                        {{ opt.text }}
                    </option>
                </select>
                <span v-if="orgBarTrail" class="org-bar-trail">{{ orgBarTrail }}</span>
            </div>

            <!-- 스텝 선택 시: 드롭다운에서 선택한 뷰(인벤토리/활동자료) 표시 -->
            <div v-if="selectedStep" class="step-content-section">
                <InventoryMain
                    v-show="stepViewMode === 'inventory'"
                    ref="inventoryMain"
                    :is-active="stepViewMode === 'inventory'"
                    :login-user="loginUser"
                    :step-idx="selectedStep.stepIdx"
                    @refresh="loadAnnualSteps"
                />
                <ActivityDataManager
                    v-show="stepViewMode === 'activity'"
                    ref="activityDataManager"
                    :login-user="loginUser"
                    :fixed-period="selectedStep.period"
                    :fixed-org-idx="selectedWorkflow?.workflow?.orgIdx ?? null"
                    @data-imported="handleActivityDataLoaded"
                />
            </div>

            <!-- 스텝 미선택: 연간 워크플로우 + 월별 산정 현황 -->
            <template v-else>
                <!-- 상단: 연간 워크플로우 -->
                <div class="annual-workflow-section">
                    <AnnualWorkflowCards
                        ref="annualWorkflowCards"
                        :steps="filteredAnnualSteps"
                        :selected-year="selectedYear"
                        :org-options="orgOptions"
                        :login-user="loginUser"
                        @step-click="handleYearSelect"
                        @refresh="loadAnnualSteps"
                        @clear-selection="selectedYear = null"
                    />
                </div>

                <!-- 하단: 월별 프로세스 현황 -->
                <div v-if="selectedYear" class="monthly-timeline-section">
                    <div class="section-header-row">
                        <div class="section-title-wrap">
                            <h2 class="section-title">월별 산정 현황</h2>
                            <span id="marker-legend-trigger" class="info-icon">
                                <i class="fas fa-info-circle"></i>
                            </span>
                            <b-popover target="marker-legend-trigger" triggers="hover" placement="top">
                                <template #title>상태별 마커 설명</template>
                                <div class="marker-legend">
                                    <p class="legend-title">월별 단계 상태 흐름</p>
                                    <ul class="legend-list">
                                        <li>
                                            <i class="fas fa-clock legend-icon icon-pending"></i>
                                            <strong>대기</strong> · 수집 대기
                                        </li>
                                        <li>
                                            <i class="fas fa-sync-alt legend-icon icon-collecting"></i>
                                            <strong>수집중</strong> · 활동자료 수집 진행
                                        </li>
                                        <li>
                                            <i class="fas fa-clipboard-check legend-icon icon-done"></i>
                                            <strong>수집완료</strong> · 산정 대기
                                        </li>
                                        <li>
                                            <i class="fas fa-calculator legend-icon icon-calc"></i>
                                            <strong>산정완료</strong> · 검증 대기
                                        </li>
                                        <li>
                                            <i class="fas fa-check legend-icon icon-verified"></i>
                                            <strong>검증완료</strong> · 최종 완료
                                        </li>
                                    </ul>
                                    <p class="legend-flow">
                                        대기 → 수집중 → 수집완료 → (산정) 대기 → 산정완료 → 검증완료
                                    </p>
                                </div>
                            </b-popover>
                        </div>
                    </div>
                    <div class="timeline-fill-wrapper">
                        <WorkflowTimeline
                            :steps="monthlySteps"
                            :period-stat="selectedWorkflow?.workflow?.periodStat ?? null"
                            :org-options="orgOptions"
                            :hide-title="true"
                            :selected-step-idx="selectedStep?.stepIdx ?? null"
                            @refresh="loadAnnualSteps"
                            @step-click="handleStepClick"
                        />
                    </div>
                </div>

                <!-- 선택된 연도가 없을 때 -->
                <div v-else class="empty-state">
                    <i class="fas fa-calendar-alt"></i>
                    <p>워크플로우를 선택하여 월별 프로세스를 확인하세요</p>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import AnnualWorkflowCards from "../views/AnnualWorkflowCards.vue";
import WorkflowTimeline from "../views/WorkflowTimeline.vue";
import InventoryMain from "../../inventory/InventoryMain.vue";
import ActivityDataManager from "../../activity/containers/ActivityDataManager.vue";
import carbonApi from "../../../api";
import { unwrapResponse } from "../../../utils/api.js";

export default {
    name: "CalcWorkflowMain",
    components: {
        AnnualWorkflowCards,
        WorkflowTimeline,
        InventoryMain,
        ActivityDataManager,
    },
    props: {
        loginUser: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            selectedOrg: null,
            selectedYear: null,
            /** 월별 타임라인에서 클릭한 스텝 (인벤토리 영역에 stepIdx 전달) */
            selectedStep: null,
            annualSteps: [],
            orgOptions: [],
            /** API 워크플로우 목록에 등장하는 orgIdx 집합 (조직 드롭다운 옵션용) */
            workflowOrgIdxSet: new Set(),
            /** 스텝 드롭다운에서 선택한 뷰: inventory | activity */
            stepViewMode: "inventory",
        };
    },
    mounted() {
        this.loadAnnualSteps();
        this.loadOrganizations();
    },
    watch: {
        filteredOrgOptions: {
            handler(opts) {
                if (opts?.length === 1 && (this.selectedOrg == null || this.selectedOrg === "")) {
                    this.selectedOrg = opts[0].value;
                }
            },
            immediate: true,
        },
        filteredAnnualSteps(newSteps) {
            // 조직별 연도 목록이 있고 선택된 연도가 없으면 첫 번째 연도 자동 선택
            if (this.selectedOrg && newSteps.length > 0 && !this.selectedYear) {
                const currentYear = new Date().getFullYear();
                const currentYearStep = newSteps.find((s) => s.year === currentYear);
                if (currentYearStep) {
                    this.handleYearSelect(currentYearStep);
                } else {
                    this.handleYearSelect(newSteps[newSteps.length - 1]);
                }
            }
        },
        selectedOrg() {
            this.selectedYear = null;
            this.selectedStep = null;
        },
        selectedYear() {
            this.selectedStep = null;
        },
    },
    methods: {
        async loadAnnualSteps() {
            try {
                // 인벤토리 워크플로우 목록 조회
                const response = await carbonApi.workflow.getInventoryWorkflows({
                    // 필요시 파라미터 추가 가능
                    // orgCode: this.loginUser?.orgCode,
                    // limit: 100,
                    // page: 1,
                });
                const data = unwrapResponse(response, { defaultMessage: "워크플로우 목록을 불러오지 못했습니다." });

                // API 응답 구조: { success: true, data: [...] }
                // unwrapResponse는 payload?.data ?? payload를 반환하므로 배열이 반환됨
                const workflows = Array.isArray(data) ? data : data?.items || data?.data || [];

                // API 응답의 workflow 목록에서 orgIdx 수집 (조직 선택 옵션용)
                const orgIdxSet = new Set();
                workflows.forEach((workflow) => {
                    if (workflow.orgIdx != null) {
                        orgIdxSet.add(workflow.orgIdx);
                    }
                });
                this.workflowOrgIdxSet = orgIdxSet;

                // 조직 정보 조회
                const orgMap = new Map();
                if (orgIdxSet.size > 0) {
                    try {
                        const orgResponse = await carbonApi.organization.getOrgs(1, 100);
                        const orgData = unwrapResponse(orgResponse, {
                            defaultMessage: "조직 정보를 불러오지 못했습니다.",
                        });
                        const orgs = Array.isArray(orgData) ? orgData : orgData?.items || orgData?.data || [];
                        orgs.forEach((org) => {
                            orgMap.set(org.orgIdx, org);
                        });
                    } catch (error) {
                        console.error("조직 정보 조회 실패:", error);
                    }
                }

                // 워크플로우별로 항목 생성 (연도만 키로 묶지 않음 → 조직별로 모두 표시)
                const steps = workflows.map((workflow) => {
                    const year = workflow.fromMonth
                        ? parseInt(workflow.fromMonth.substring(0, 4), 10)
                        : workflow.year || new Date().getFullYear();
                    const org = workflow.orgIdx ? orgMap.get(workflow.orgIdx) : null;
                    const workflowWithOrg = { ...workflow, org };
                    const periodStat = workflow.periodStat || "READY";
                    return {
                        year,
                        status: periodStat,
                        workflowIdx: workflow.workflowIdx,
                        workflow: workflowWithOrg,
                    };
                });
                this.annualSteps = steps.sort((a, b) => a.year - b.year);

                // 데이터가 없으면 기본 데이터 사용
                if (this.annualSteps.length === 0) {
                    this.workflowOrgIdxSet = new Set();
                    const currentYear = new Date().getFullYear();
                    this.annualSteps = [
                        { year: currentYear - 2, status: "CONFIRMED" },
                        { year: currentYear - 1, status: "CONFIRMED" },
                        { year: currentYear, status: "COLLECTING" },
                    ];
                }
            } catch (error) {
                console.error("워크플로우 목록 조회 실패:", error);
                // 에러 발생 시 기본 데이터 사용
                this.workflowOrgIdxSet = new Set();
                const currentYear = new Date().getFullYear();
                this.annualSteps = [
                    { year: currentYear - 2, status: "CONFIRMED" },
                    { year: currentYear - 1, status: "CONFIRMED" },
                    { year: currentYear, status: "COLLECTING" },
                ];
            }
        },
        async loadOrganizations() {
            try {
                const response = await carbonApi.organization.getOrgs();
                const data = unwrapResponse(response, { defaultMessage: "조직 목록을 불러오지 못했습니다." });
                let orgs = [];
                if (Array.isArray(data)) {
                    orgs = data;
                } else if (data?.organizations && Array.isArray(data.organizations)) {
                    orgs = data.organizations;
                } else if (data?.items && Array.isArray(data.items)) {
                    orgs = data.items;
                } else if (data?.data && Array.isArray(data.data)) {
                    orgs = data.data;
                }
                this.orgOptions = orgs.map((org) => ({
                    value: org.orgIdx,
                    text:
                        `${org.orgName || ""}${org.orgCode ? ` (${org.orgCode})` : ""}`.trim() || `조직 #${org.orgIdx}`,
                }));
            } catch (error) {
                console.error("조직 목록 조회 실패:", error);
                this.orgOptions = [];
            }
        },
        handleYearSelect(step) {
            this.selectedYear = step.year;
        },
        handleStepClick(payload) {
            const { step, viewMode } = payload && typeof payload === "object" ? payload : { step: payload, viewMode: "inventory" };
            this.selectedStep = step;
            this.stepViewMode = viewMode || "inventory";
        },
        handleActivityDataLoaded() {
            this.loadAnnualSteps();
        },
        /** 뒤로가기: 스텝 선택 중이면 워크플로우 뷰로, 아니면 조직 선택으로 */
        handleOrgBarBack() {
            if (this.selectedStep) {
                this.selectedStep = null;
            } else {
                this.selectedOrg = null;
            }
        },
        openCreateWorkflowModal() {
            // 인벤토리(스텝) 화면일 때는 카드 뷰로 돌아온 뒤 모달 오픈
            if (this.selectedStep) {
                this.selectedStep = null;
                this.$nextTick(() => {
                    this.$refs.annualWorkflowCards?.openCreateModal?.();
                });
            } else {
                this.$refs.annualWorkflowCards?.openCreateModal?.();
            }
        },
    },
    computed: {
        /** API 워크플로우 목록에 등장한 orgIdx 기준 조직 옵션 (진입·상단바 드롭다운 공용) */
        filteredOrgOptions() {
            if (!this.workflowOrgIdxSet?.size) return this.orgOptions;
            return this.orgOptions.filter((o) => this.workflowOrgIdxSet.has(Number(o.value)));
        },
        filteredAnnualSteps() {
            if (this.selectedOrg == null || this.selectedOrg === "" || !this.annualSteps?.length) return [];
            const sel = Number(this.selectedOrg);
            return this.annualSteps.filter((s) => Number(s.workflow?.orgIdx) === sel);
        },
        /** 상단 바: 조직은 드롭다운, 이하 경로만 표시 (워크플로우명 / 스텝 period) */
        orgBarTrail() {
            const selectedWorkflowname = this.selectedWorkflow?.workflow.workflowName;
            if (!selectedWorkflowname) return "";

            const stepPeriod = this.selectedStep?.period
                ? `${this.selectedStep.period.substring(0, 4)}.${this.selectedStep.period.substring(4, 6)}`
                : "";
            if (!stepPeriod) return ` / ${selectedWorkflowname}`;
            return ` / ${selectedWorkflowname} / ${stepPeriod}`;
        },
        selectedWorkflow() {
            if (!this.selectedYear || !this.filteredAnnualSteps?.length) return null;
            return this.filteredAnnualSteps.find((s) => s.year === this.selectedYear);
        },
        monthlySteps() {
            const workflow = this.selectedWorkflow?.workflow;
            const steps = workflow?.steps;
            if (!steps || !Array.isArray(steps)) return [];

            return steps
                .map((s) => {
                    const period = s.period || "";
                    const month = period.length >= 6 ? parseInt(period.substring(4, 6), 10) : 0;
                    let status = "READY";
                    if (s.calcStat === "VERIFIED") status = "VERIFIED";
                    else if (s.calcStat === "CALCULATED") status = "CALCULATED";
                    else if (s.collStat === "COMPLETED") status = "COLLECTING";
                    else if (s.collStat === "COLLECTING") status = "COLLECTING";

                    return {
                        stepIdx: s.stepIdx,
                        year: month,
                        month,
                        status,
                        workflow: {
                            workflowName: `${month}월`,
                            fromMonth: period,
                            toMonth: period,
                            description: null,
                        },
                        period,
                        collStat: s.collStat || "PENDING",
                        calcStat: s.calcStat || "PENDING",
                        isCheckpoint: !!s.isCheckpoint,
                        totalEmissions: s.totalEmissions != null ? s.totalEmissions : null,
                        scope1Emissions: s.scope1Emissions != null ? s.scope1Emissions : null,
                        scope2Emissions: s.scope2Emissions != null ? s.scope2Emissions : null,
                        scope3Emissions: s.scope3Emissions != null ? s.scope3Emissions : null,
                    };
                })
                .sort((a, b) => a.month - b.month);
        },
    },
};
</script>

<style scoped>
.calc-workflow-main {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 12px 22px;
    background: #fafafa;
    gap: 10px;
}

.calc-workflow-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

/* 조직 선택 (선택 전) */
.org-selector {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
}

.org-selector-card {
    width: 100%;
    max-width: 360px;
    padding: 32px 28px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
    text-align: center;
}

.org-selector-icon {
    font-size: 32px;
    color: #10b981;
    margin-bottom: 12px;
}

.org-selector-title {
    font-size: 15px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 6px 0;
}

.org-selector-desc {
    font-size: 12px;
    color: #64748b;
    margin: 0 0 20px 0;
    line-height: 1.5;
}

.org-selector select.org-select {
    width: 100%;
    padding: 10px 36px 10px 14px;
    font-size: 13px;
    font-weight: 500;
    color: #1e293b;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
}

.org-selector select.org-select:hover {
    border-color: #10b981;
}

.org-selector select.org-select:focus {
    outline: none;
    border-color: #10b981;
}

/* 조직 선택 후 바 */
.org-bar {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    font-size: 14px;
    font-weight: 600;
}

.org-bar-icon {
    color: #94a3b8;
}

.org-bar-select {
    font-size: 14px;
    font-weight: 600;
    color: #64748b;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 4px 8px;
    cursor: pointer;
    min-width: 120px;
    max-width: 200px;
}

.org-bar-select:hover,
.org-bar-select:focus {
    border-color: #10b981;
    color: #334155;
    outline: none;
}

.org-bar-trail {
    color: #64748b;
    font-weight: 600;
}

.org-bar-back {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 24px;
    height: 24px;
    padding: 0;

    color: #64748b;
    background: none;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.org-bar-back:hover {
    color: #10b981;
}

/* 연간 워크플로우 섹션 */
.annual-workflow-section {
    flex-shrink: 0;
}

/* 월별 타임라인 섹션 */
.monthly-timeline-section {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: visible; /* 드롭다운 메뉴가 잘리지 않도록 */
}

.timeline-fill-wrapper {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    overflow: visible; /* 드롭다운 메뉴가 잘리지 않도록 */
}

/* 스텝 선택 시 인벤토리/활동자료 뷰 */
.step-content-section {
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
}

/* 자식(인벤토리/활동)이 calc-workflow-main 높이 안에서만 쓰도록 */
.step-content-section > * {
    flex: 1 1 0;
    min-height: 0;
    min-width: 0;
}

.section-header-row {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.section-title-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
}

.section-header-row .section-title {
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: 0.5px;
    margin: 0;
    padding-left: 12px;
    position: relative;
}

.info-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    cursor: help;
    font-size: 14px;
    transition: color 0.2s;
}

.info-icon:hover {
    color: #52b788;
}

.section-header-row .section-title::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 16px;
    background: #10b981;
    border-radius: 2px;
}

.empty-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    gap: 16px;
}

.empty-state i {
    font-size: 100px;
    color: #cbd5e1;
}

.empty-state p {
    font-size: 20px;
    margin: 0;
    font-weight: 500;
}

/* 팝오버 슬롯 (slot은 부모 템플릿에 있어 scoped 적용됨) */
.marker-legend {
    font-size: 13px;
    line-height: 1.6;
    max-width: 280px;
}
.marker-legend .legend-title {
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 10px 0;
    font-size: 12px;
}
.marker-legend .legend-list {
    list-style: none;
    padding: 0;
    margin: 0 0 10px 0;
}
.marker-legend .legend-list li {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
    color: #475569;
}
.marker-legend .legend-icon {
    width: 16px;
    text-align: center;
    font-size: 12px;
}
.marker-legend .legend-icon.icon-pending {
    color: #94a3b8;
}
.marker-legend .legend-icon.icon-collecting,
.marker-legend .legend-icon.icon-done,
.marker-legend .legend-icon.icon-calc,
.marker-legend .legend-icon.icon-verified {
    color: #10b981;
}
.marker-legend .legend-flow {
    font-size: 11px;
    color: #94a3b8;
    margin: 0;
    padding-top: 8px;
    border-top: 1px solid #e2e8f0;
}

@media (max-width: 1200px) {
    .calc-workflow-main {
        padding: 12px;
        gap: 12px;
    }
}
</style>
